import { cloneDeep, matchesProperty, merge } from "lodash";
import { defineStore } from "pinia";
import { instance } from "@/api";
import { getRoles, getDetails, updateUser } from "@/api/apiclient/user";

import { clearToken, keycloak, logout } from "@/utilities/keycloak";
import { LocalStorage } from "@/storage";

import type { privilegeType, userType } from "@/types/user";
import type { profileAgreementType } from "@/types/agreements";
import { getList } from "@/api/apiclient/project";
import {
  addProfileSetting,
  deleteProfileSetting,
  getProfileSetting,
} from "@/api/apiclient/profile";
import { permissionType } from "@/types/permission";
import { useSettings } from "./settings";
import { useNetwork } from "./network";

const model: userType = {
  acceptedAgreements: [],
  avatar: null,
  bio: null,
  dob: null,
  email: null,
  enabled: true,
  firstName: null,
  fiscalCode: '',
  id: null,
  lastName: null,
  password: null,
  phone: null,
  IdentityInfos: {
    email: null,
    email_verified: null,
    family_name: null,
    given_name: null,
    name: null,
    preferred_username: null,
    sub: null,
  },
  profileAgreement: [],
  organizations: [],
  selectedSkills: [],
  userType: null,
  skillDescription: null,
  role: null,
};

const professionalCategories = [
  { key: "Surveyor", label: "Geometra" },
  { key: "Architect", label: "Architetto" },
  { key: "Engineer", label: "Ingegnere" },
  { key: "StructuralEngineer", label: "Ingegnere strutturale" },
  { key: "BusinessConsultant", label: "Consulente aziendale" },
  { key: "Expert", label: "Perito" },
];

export const useUser = defineStore("user", {
  state: () => {
    return {
      // If the model evolves into having nested references we avoid gotchas
      current: cloneDeep(model),
      refreshToken: null as string | null | undefined,
      token: null as string | null | undefined,
      idToken: null as string | null | undefined,
      roles: [] as any[],
      allRoles: [] as any[],
      network: {} as {
        code: string;
        id: number;
        name: string;
        type: string;
      },
      missingAgreements: [] as {
        content: string;
        description: string;
        id: number;
        isMandatory: boolean;
        path: null | string;
        title: null | string;
        type: string;
        version: number;
      }[],
      role_id: null,
      currentRole: null as string | null | undefined,
      privileges: [] as Array<privilegeType>,
      has_bank_service_projects: false,
      savedRole: null as { id: number; name: string } | null,
      savedNetwork: null as { id: number; name: string } | null,
      availableRoles: [] as Array<{ id: number; name: string }>,
      permissions: [] as Array<permissionType>,
    };
  },
  actions: {
    setCurrent(item: userType): void {
      this.current = merge(this.current, item);
    },
    resetCurrent(): void {
      this.current = cloneDeep(model);
    },
    async getCurrent(params: { withIdentity: boolean; id?: number } = { withIdentity: false }) {
      const item = await getDetails(params);
      this.setCurrent(item);
      return item;
    },
    setTokens(keycloak: any): void {
      this.token = keycloak.token;
      this.refreshToken = keycloak.refreshToken;
      this.idToken = keycloak.idToken;
      instance.defaults.headers.Authorization = `Bearer ${keycloak.token}`;
      LocalStorage.set(["auth", "token"], keycloak.token);
      LocalStorage.set(["auth", "refreshToken"], keycloak.refreshToken);
      LocalStorage.set(["auth", "idToken"], keycloak.idToken);
    },
    resetTokens() {
      this.token = null;
      this.refreshToken = null;
      delete instance.defaults.headers.Authorization;
    },
    setCurrentRole(role: string): void {
      this.currentRole = role;
    },
    composeAcceptedAgreements() {
      this.current.profileAgreement.forEach((i: profileAgreementType) => {
        if (i.agreement.id) {
          this.current.acceptedAgreements.push(i.agreement.id);
        }
      });
    },
    async update(): Promise<void> {
      const item = await updateUser(this.current);
      this.setCurrent(item);
      return item;
    },
    async logout(): Promise<void> {
      await logout();
      this.resetCurrent();
      this.resetTokens();
      clearToken();
      // await router.push({ name: "welcome" });
      // const page = { name: "welcome" };
      // await router.push(page);
      // this.resetCurrent();
      // await keycloak.logout();
      // this.resetTokens();
    },
    async getRoles(): Promise<void> {
      try {
        // this.allRoles = roles;
        // TODO: Remove hardcoded application_id
        // this.roles = roles.filter((r) => r.application_id === 2 || r.application_id === null);

        const networkStore = useNetwork();

        const {
          roles: rolesWithStatus,
          missingAgreements: missingAgreements,
          privileges: privileges,
          permissions: permissions,
          lastUserRoleId: lastUserRoleId
        } = await useSettings().init();

        this.permissions = permissions;
        this.privileges = privileges;
        
        const [, roles] = await Promise.all([
          networkStore.loadList(),
          getRoles()
        ]);

        roles.forEach((role: any) => {
          const current = rolesWithStatus.find((r) => r.id === role.roleId);
          if (current) {
            role.status = current.status;
            role.roleName = current.name;
            role.isSystemRole = current.isSystemRole;
            role.page = current.page;
          }
        });

        this.availableRoles = rolesWithStatus
          .filter((r) => !r.isSystemRole)
          .map((r) => {
            return {
              id: r.id,
              name: r.name,
            };
          });

        this.roles = roles;
        this.missingAgreements = missingAgreements;

        // const settingsLastUsedRole = await getProfileSetting("lastUsedRole");

        if (lastUserRoleId) {
          const roleFound = this.roles.find((r) => r.roleId == lastUserRoleId) || null;
          if (roleFound) {
            this.setSavedRole({ id: roleFound.roleId, name: roleFound.roleName });
          }
        } else {
          const activeRoles = this.roles.filter((r) => r.status === "Active" && !r.isSystemRole);
          if (activeRoles.length > 0) {
            const roleFound = this.roles.find((r) => r.roleName == activeRoles[0].roleName);
            if (roleFound) {
              await this.updateSavedRole(roleFound.roleId);
              this.setSavedRole({ id: roleFound.roleId, name: roleFound.roleName });
            }
          }
        }
        // await this.deleteProfileSetting("lastSelectedNetwork");
        // debugger;
        const settingsLastUsedNetwork = await getProfileSetting("lastSelectedNetwork");

        if (settingsLastUsedNetwork.length > 0) {
          const networkFound = networkStore.list.find((r) => r.id == Number(settingsLastUsedNetwork[0].value) ) || null;
          if (networkFound) {
            this.setSavedNetwork({ id: networkFound.id, name: networkFound.name });
          }
        } else {
          if ( networkStore.list?.length > 0 && networkStore.list[0]) {
            await this.updateSavedNetwork(networkStore.list[0]?.id);
            this.setSavedNetwork({ id: networkStore.list[0]?.id, name: networkStore.list[0]?.name });
          }
        }

      } catch (error) {
        console.error(error);
      }
    },
    setSavedRole(role: { id: number; name: string }) {
      this.savedRole = { ...role };
      this.setCurrentRole(role.id);
    },
    async deleteProfileSetting(settingName: string) {
      const settings = await getProfileSetting(settingName);
      if (settings.length > 0) {
        await deleteProfileSetting(settings[0].id);
      }
    },
    async updateSavedRole(roleId: number) {
      await this.deleteProfileSetting("lastUsedRole");
      this.savedRole = null;
      return addProfileSetting({
        section: "lastUsedRole",
        setting: "id",
        value: `${roleId}`,
      });
    },
    setSavedNetwork(network: { id: number; name: string }) {
      this.savedNetwork = { ...network };
    },
    async updateSavedNetwork(networkId: number) {
      await this.deleteProfileSetting("lastSelectedNetwork");
      this.savedNetwork = null;
      return addProfileSetting({
        section: "lastSelectedNetwork",
        setting: "id",
        value: `${networkId}`,
      });
    },
    async searchBankServiceProject(): Promise<any> {
      const list = await getList(
        { type: "TechValidation" },
        {
          headers: {
            "X-AppRole": 103,
          },
        }
      );
      if (list.data && list.data.length > 0) {
        this.has_bank_service_projects = true;
      }
    },
    // function to check if a certain id is the one of the currently logged user
    isLoggedUser(id: string) {
      return this.current.id === id;
    },
  },
  getters: {
    isLoggedIn: (): boolean => {
      return keycloak.token != null;
    },
    getSavedRoleName: (state) => {
      return state.savedRole?.name.toLowerCase() || "";
    },
    getSavedNetworkName: (state) => {
      return state.savedNetwork?.name.toLowerCase() || "";
    },
    isFinalUser: (state) => state.roles.some(matchesProperty("roleName", "FinalUser")),
    nextIncompleteRole: (state) => state.roles.find(matchesProperty("status", "Pending")),
    isSuspended: (state): boolean =>
      state.roles.some(
        matchesProperty("status", "Suspended")
      ) /* if user has at least one role with Suspended */,
    isPending: (state): boolean => state.roles.some(matchesProperty("status", "Pending")),
    isWaiting: (state) =>
      state.roles.some(
        matchesProperty("status", "Waiting")
      ) /* if user has at least one role with Waiting */,
    isDesigner: (state): boolean => {
      const res = state.roles.filter(
        (r) => r.status == "Active" && ["Designer"].includes(r.roleName)
      );
      return !!res.length;
    },
    isCapoHub: (state): boolean => {
      const res = state.roles.filter(
        (r) => r.status == "Active" && ["HubHead"].includes(r.roleName)
      );
      return !!res.length;
    },
    isSpecialist: (state): boolean => {
      const res = state.roles.filter(
        (r) => r.status == "Active" && ["Specialist"].includes(r.roleName)
      );
      return !!res.length;
    },
    isThermotechnician: (state): boolean => {
      const res = state.roles.filter(
        (r) => r.status == "Active" && ["Thermotechnician"].includes(r.roleName)
      );
      return !!res.length;
    },
    isAsseverator: (state): boolean => {
      const res = state.roles.filter(
        (r) => r.status == "Active" && ["Asseverator"].includes(r.roleName)
      );
      return !!res.length;
    },
    isSecurity: (state): boolean => {
      const res = state.roles.filter(
        (r) => r.status == "Active" && ["Security"].includes(r.roleName)
      );
      return !!res.length;
    },
    isCommercialUser: (state): boolean => {
      const commercialRoles = ["Coach", "Specialist", "Gestori"]; //Todo inserire codice Gestori
      if ( state.savedRole?.name) {
        return commercialRoles.includes( state.savedRole?.name);
      } else {
        const res = state.roles.filter((r) => r.role_id != 1);
        if (res.length == 0) {
          return false;
        }
        return res[0].status == "Active" && commercialRoles.includes(res[0].roleName);
      }
    },
    isBackofficeUser: (state): boolean => {
      console.log( state.savedRole?.name);
      if ( state.savedRole?.name) {
        return  state.savedRole?.name == "BackofficeUser";
      } else {
        const res = state.roles.filter((r) => r.role_id != 1);
        if (res.length == 0) {
          return false;
        }
        return res[0].status == "Active" && res[0].roleName == "BackofficeUser";
      }
    },
    isBackofficeAdmin: (state): boolean => {
      if ( state.savedRole?.name) {
        return  state.savedRole?.name == "BackofficeAdmin";
      } else {
        const res = state.roles.filter((r) => r.role_id != 1);
        if (res.length == 0) {
          return false;
        }
        return res[0].status == "Active" && res[0].roleName == "BackofficeAdmin";
      }
    },
    hasSelectedDesignerRole(): boolean {
      return this.getSavedRoleName.toLowerCase() == "designer";
    },
    hasSelectedThermotechnicianRole(): boolean {
      return this.getSavedRoleName.toLowerCase() == "thermotechnician";
    },
    hasSelectedAsseveratorRole(): boolean {
      return this.getSavedRoleName.toLowerCase() == "asseverator";
    },
    hasSelectedSecurityRole(): boolean {
      return this.getSavedRoleName.toLowerCase() == "security";
    },
    hasSelectedSpecialistRole(): boolean {
      return this.getSavedRoleName.toLowerCase() == "specialist";
    },
    hasSelectedBackofficeUserRole(): boolean {
      return this.getSavedRoleName.toLowerCase() == "backofficeuser";
    },
    hasSelectDesignerMacroRole(): boolean {
      // Return if user has select designer role or similar role with same behavior
      return this.hasSelectedDesignerRole || this.hasSelectedThermotechnicianRole || this.hasSelectedAsseveratorRole || this.hasSelectedSecurityRole;
    },
    isUserWithRelevantActiveAccess(): boolean {
      if (this.isPending || this.isSuspended) return false;
      return this.isBackofficeAdmin || this.isBackofficeUser;
    },
    getHigherRoleId: (state): number => {
      if ( state.savedRole?.name) {
        return state.roles.find((r) => r.roleName ===  state.savedRole?.name)?.role_id || 1;
      } else {
        return state.roles.filter((r) => r.role_id != 1 && r.status === "Active").length > 0
          ? state.roles
              .filter((r) => r.role_id != 1)
              .reduce((max, r) => (r.role_id > max ? r.role_id : max), 1)
          : 1;
      }
    },
    getHigherRoleName: (state): string => {
      if ( state.savedRole?.name) {
        return  state.savedRole?.name;
      } else {
        const activeRoles = state.roles.filter((r) => r.role_id != 1 && r.status === "Active");
        if (activeRoles.length > 0) {
          const highestRole = activeRoles.reduce(
            (max, r) => (r.role_id > max.role_id ? r : max),
            activeRoles[0]
          );
          return highestRole.roleName;
        } else {
          return "-";
        }
      }
    },
    isHidden: (state) => {
      return (attribute: string, resource: string): boolean => {
        if (state.privileges.length == 0) return false;
        const res = state.privileges
          ?.find((p) => p.resource == resource)
          ?.attributes?.find((a) => a.name.toLowerCase() == attribute.toLowerCase());
        return res?.status == "Hidden";
      };
    },
    isDisabled: (state) => {
      return (attribute: string, resource: string): boolean => {
        if (state.privileges.length == 0) return false;
        const res = state.privileges
          ?.find((p) => p.resource == resource)
          ?.attributes?.find((a) => a.name.toLowerCase() == attribute.toLowerCase());
        return res?.status == "Disabled";
      };
    },
    isVisible: (state) => {
      return (attribute: string, resource: string): boolean => {
        if (state.privileges.length == 0) return false;
        const res = state.privileges
          ?.find((p) => p.resource == resource)
          ?.attributes?.find((a) => a.name.toLowerCase() == attribute.toLowerCase());
        return res?.status == "Enabled";
      };
    },
    hasWaitingRole: (state) => {
      return (roleName: string) =>
        state.roles.filter((r) => r.status == "Waiting" && [roleName].includes(r.roleName))
          ?.length > 0;
    },
    hasAccessToApplication: (state): boolean => {
      if (state.roles?.length > 0) {
        return !state.roles.every((r) => !!r.isSystemRole);
      } else {
        return false;
      }
    },
    professionalCategoryOptions: () =>
      professionalCategories
        .map((value) => {
          return {
            text: value.label,
            value: value.key,
          };
        })
        .sort((a, b) => (a.text > b.text ? 1 : -1)),
    getPermissions(): Array<permissionType> {
      return this.permissions;
    },
  },
});
